import { FC, useEffect, useMemo, useState } from 'react';

import { Link, useForm, usePage } from '@inertiajs/react';

import { buildInitialForm, normalizeMethods } from './checkoutHelpers';
import { CheckoutInfoBanner } from './CheckoutInfoBanner';
import {
    DeliveryMethod,
    SavedProfile,
    SellerDeliveryForm,
    SellerGroup,
} from './checkoutTypes';
import { OrderFinalActions } from './OrderFinalActions';
import { OrderSummary } from './OrderSummary';
import { SellerCheckoutCard } from './SellerCheckoutCard';
import { CartItem, useCart } from '../../context/cart-context';
import { Routes } from '../../models/routes';
import { OfferProperty, ShippingMethod } from '../../types';

type PageProps = {
    errors?: {
        general: string;
    };
    availableShippingMethods?: ShippingMethod[];
    offer_properties?: OfferProperty[];
    saved_delivery_profiles?: {
        nova_poshta: SavedProfile | null;
        ukrposhta: SavedProfile | null;
    };
};

type CheckoutFormState = {
    cart_items: Array<{
        id: string | number;
        quantity: number;
        offer_id?: number;
        price: number;
    }>;
    seller_deliveries: Record<string, Record<string, string>>;
    remember_delivery_details: boolean;
};

const New: FC = () => {
    const { cartItems, totalPrice, clearCart, removeFromCart } = useCart();
    const {
        errors,
        saved_delivery_profiles,
        availableShippingMethods,
        offer_properties: offerProperties = [],
    } = usePage<PageProps>().props;
    const savedProfiles = saved_delivery_profiles || {
        nova_poshta: null,
        ukrposhta: null,
    };
    const availableMethods = useMemo(
        () =>
            normalizeMethods(availableShippingMethods, [
                'nova_poshta',
                'pickup',
            ]),
        [availableShippingMethods],
    );

    const [rememberDelivery, setRememberDelivery] = useState(true);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [sellerForms, setSellerForms] = useState<
        Record<string, SellerDeliveryForm>
    >({});
    const [expandedDeliveryEditors, setExpandedDeliveryEditors] = useState<
        Record<string, boolean>
    >({});
    const [itemActionsOpen, setItemActionsOpen] = useState<
        Record<string, boolean>
    >({});
    const { post, processing, transform } = useForm<CheckoutFormState>({
        cart_items: [],
        seller_deliveries: {},
        remember_delivery_details: true,
    });

    const sellerGroups = useMemo<SellerGroup[]>(() => {
        const grouped = new Map<string, SellerGroup>();

        cartItems.forEach((item) => {
            const parsedSellerId =
                typeof item.sellerId === 'number'
                    ? item.sellerId
                    : Number(item.sellerId);
            const sellerId = Number.isFinite(parsedSellerId)
                ? parsedSellerId
                : null;
            const offerId = Number(item.offerId ?? item.id);
            const normalizedOfferId = Number.isFinite(offerId) ? offerId : null;

            const sellerName =
                item.sellerName ||
                (typeof item.name === 'string' && item.name.includes('(від ')
                    ? item.name.split('(від ')[1]?.replace(')', '').trim()
                    : 'Продавець');

            const key = sellerId
                ? `seller:${sellerId}`
                : `seller-name:${sellerName}`;
            const methods = normalizeMethods(
                item.sellerShippingMethods,
                availableMethods,
            );

            if (!grouped.has(key)) {
                grouped.set(key, {
                    key,
                    sellerId,
                    sellerName,
                    items: [item],
                    itemIds: normalizedOfferId ? [normalizedOfferId] : [],
                    allowedMethods: methods,
                });
                return;
            }

            const existing = grouped.get(key)!;
            existing.items.push(item);
            if (normalizedOfferId) {
                existing.itemIds.push(normalizedOfferId);
            }
            if (existing.allowedMethods.length === 0 && methods.length > 0) {
                existing.allowedMethods = methods;
            }
        });

        return Array.from(grouped.values());
    }, [availableMethods, cartItems]);

    useEffect(() => {
        setSellerForms((prev) => {
            const next = { ...prev };

            sellerGroups.forEach((group) => {
                if (!next[group.key]) {
                    next[group.key] = buildInitialForm('', savedProfiles);
                    return;
                }

                const selectedMethod = next[group.key].method;
                if (
                    selectedMethod &&
                    !group.allowedMethods.includes(selectedMethod)
                ) {
                    next[group.key] = buildInitialForm('', savedProfiles);
                }
            });

            return next;
        });
    }, [availableMethods, sellerGroups, savedProfiles]);

    const clearFieldErrors = (
        sellerKey: string,
        fields: Array<keyof SellerDeliveryForm>,
    ) => {
        setFormErrors((prev) => {
            const next = { ...prev };
            fields.forEach((field) => {
                delete next[`${sellerKey}.${field}`];
            });
            return next;
        });
    };

    const updateField = (
        sellerKey: string,
        field: keyof SellerDeliveryForm,
        value: string,
    ) => {
        setSellerForms((prev) => {
            const baseForm =
                prev[sellerKey] || buildInitialForm('', savedProfiles);
            const nextForm: SellerDeliveryForm = {
                ...baseForm,
                [field]: value,
            };

            return {
                ...prev,
                [sellerKey]: nextForm,
            };
        });

        clearFieldErrors(sellerKey, [field]);
    };

    const patchForm = (
        sellerKey: string,
        patch: Partial<SellerDeliveryForm>,
    ) => {
        setSellerForms((prev) => {
            const baseForm =
                prev[sellerKey] || buildInitialForm('', savedProfiles);
            const nextForm: SellerDeliveryForm = {
                ...baseForm,
                ...patch,
            };

            return {
                ...prev,
                [sellerKey]: nextForm,
            };
        });

        clearFieldErrors(
            sellerKey,
            Object.keys(patch) as Array<keyof SellerDeliveryForm>,
        );
    };

    const setMethod = (sellerKey: string, method: DeliveryMethod) => {
        const prefilled = buildInitialForm(method, savedProfiles);

        setSellerForms((prev) => ({
            ...prev,
            [sellerKey]: {
                ...prefilled,
                comment_for_seller: prev[sellerKey]?.comment_for_seller || '',
            } as SellerDeliveryForm,
        }));

        setExpandedDeliveryEditors((prev) => ({
            ...prev,
            [sellerKey]: false,
        }));

        clearFieldErrors(sellerKey, ['method']);
    };

    const hasSellerWithoutMethods = sellerGroups.some(
        (group) => group.allowedMethods.length === 0,
    );

    const hasMissingDeliveryMethod = () => {
        const selectionErrors: Record<string, string> = {};

        sellerGroups.forEach((group) => {
            if (!sellerForms[group.key]?.method) {
                selectionErrors[`${group.key}.method`] =
                    'Оберіть спосіб доставки';
            }
        });

        if (Object.keys(selectionErrors).length === 0) return false;

        setFormErrors((prev) => ({ ...prev, ...selectionErrors }));
        return true;
    };

    const hasInvalidNovaPoshtaSelection = () => {
        const selectionErrors: Record<string, string> = {};

        sellerGroups.forEach((group) => {
            const form =
                sellerForms[group.key] || buildInitialForm('', savedProfiles);

            if (form.method !== 'nova_poshta') return;

            if (!form.nova_poshta_city_ref.trim()) {
                selectionErrors[`${group.key}.city`] =
                    'Оберіть місто зі списку';
            }

            if (!form.nova_poshta_warehouse_ref.trim()) {
                selectionErrors[`${group.key}.delivery_point_number`] =
                    'Оберіть відділення або поштомат зі списку';
            }
        });

        if (Object.keys(selectionErrors).length === 0) return false;

        setFormErrors((prev) => ({ ...prev, ...selectionErrors }));
        return true;
    };

    const handleSubmitOrder = () => {
        if (hasMissingDeliveryMethod()) return;
        if (hasInvalidNovaPoshtaSelection()) return;

        const sellerDeliveriesPayload: Record<string, SellerDeliveryForm> = {};

        for (const group of sellerGroups) {
            const deliveryKey =
                group.sellerId && Number.isFinite(group.sellerId)
                    ? String(group.sellerId)
                    : group.itemIds[0]
                      ? `offer:${group.itemIds[0]}`
                      : `seller-name:${group.sellerName}`;

            sellerDeliveriesPayload[deliveryKey] = sellerForms[group.key];
        }

        const sellerDeliveriesFormData = Object.fromEntries(
            Object.entries(sellerDeliveriesPayload).map(([key, form]) => [
                key,
                Object.fromEntries(
                    Object.entries(form).map(([field, value]) => [
                        field,
                        value ?? '',
                    ]),
                ),
            ]),
        ) as Record<string, Record<string, string>>;

        transform(() => ({
            cart_items: cartItems.map(({ id, quantity, offerId, price }) => ({
                id,
                quantity,
                offer_id: offerId,
                price,
            })),
            seller_deliveries: sellerDeliveriesFormData,
            remember_delivery_details: rememberDelivery,
        }));

        post(Routes.Orders, {
            onSuccess: () => {
                clearCart();
            },
            onError: (newErrors) => {
                setFormErrors(newErrors as Record<string, string>);
            },
            preserveScroll: true,
        });
    };

    const handleRemoveItem = (itemId: CartItem['id']) => {
        removeFromCart(itemId);
        setItemActionsOpen((prev) => {
            const next = { ...prev };
            delete next[String(itemId)];
            return next;
        });
    };

    const toggleItemActions = (itemId: CartItem['id']) => {
        setItemActionsOpen((prev) => ({
            ...prev,
            [String(itemId)]: !prev[String(itemId)],
        }));
    };

    if (cartItems.length === 0) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="mx-auto max-w-md text-center">
                    <h1 className="mb-4 text-2xl font-bold">
                        Ваш кошик порожній
                    </h1>
                    <p className="mb-6 text-muted-foreground">
                        Додайте товари в кошик, щоб оформити замовлення
                    </p>
                    <Link href={Routes.Home} className="btn btn-primary">
                        Повернутися до покупок
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-muted/50">
            <div className="container mx-auto px-4 py-8">
                {hasSellerWithoutMethods && (
                    <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <p className="font-medium text-amber-900">
                            Один або кілька продавців не налаштували методи
                            доставки. Завершити checkout для цих товарів зараз
                            неможливо.
                        </p>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-8 xl:grid-cols-3">
                    <div className="space-y-6 xl:col-span-2">
                        <CheckoutInfoBanner />

                        {sellerGroups.map((group) => {
                            const form =
                                sellerForms[group.key] ||
                                buildInitialForm('', savedProfiles);

                            return (
                                <SellerCheckoutCard
                                    key={group.key}
                                    group={group}
                                    form={form}
                                    savedDeliveryProfiles={savedProfiles}
                                    expandedDeliveryEditor={
                                        !!expandedDeliveryEditors[group.key]
                                    }
                                    formErrors={formErrors}
                                    itemActionsOpen={itemActionsOpen}
                                    onToggleItemActions={toggleItemActions}
                                    onRemoveItem={handleRemoveItem}
                                    onSetMethod={setMethod}
                                    onExpandDeliveryEditor={(sellerKey) =>
                                        setExpandedDeliveryEditors((prev) => ({
                                            ...prev,
                                            [sellerKey]: true,
                                        }))
                                    }
                                    onUpdateField={updateField}
                                    onPatchForm={patchForm}
                                    offerProperties={offerProperties}
                                />
                            );
                        })}

                        <label className="flex cursor-pointer items-center gap-2">
                            <input
                                type="checkbox"
                                checked={rememberDelivery}
                                onChange={(e) =>
                                    setRememberDelivery(e.target.checked)
                                }
                                className="h-4 w-4 rounded border-gray-300 accent-primary"
                            />
                            <span className="text-sm text-muted-foreground">
                                Запам&apos;ятати дані доставки для наступних
                                замовлень
                            </span>
                        </label>
                    </div>

                    <div className="xl:col-span-1">
                        <div className="sticky top-8 space-y-6">
                            <OrderSummary
                                totalPrice={totalPrice}
                                items={cartItems}
                                offerProperties={offerProperties}
                                message={errors?.general}
                            />
                            <OrderFinalActions
                                handleSubmitOrder={handleSubmitOrder}
                                isProcessing={processing}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default New;
